import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import { createStudentAsync, clearError } from '../store/slices/studentsSlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import axios from 'axios';
import StudentReferrerPicker, { formatStudentName, type ReferrerOption } from './StudentReferrerPicker';

import './GroupForm.css';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

const CreateStudent: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { loading, error } = useAppSelector((state) => state.students);
  const { token } = useAppSelector((state) => state.auth);
  const { groups } = useAppSelector((state) => state.groups);

  const fromConsultationId = searchParams.get('fromConsultation');

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    surname: '',
    groupId: '',
    contacts: '',
    startDate: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [referrerOption, setReferrerOption] = useState<ReferrerOption | null>(null);
  const groupsLoadedRef = useRef(false);

  useEffect(() => {
    if (!groupsLoadedRef.current) {
      dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
      groupsLoadedRef.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    if (!fromConsultationId || !token) return;
    axios.get(`${API_BASE_URL}/consultations/${fromConsultationId}`, {
      headers: { Authorization: `Bearer ${token}` }
    }).then(res => {
      const c = res.data.consultation;
      setFormData(prev => ({
        ...prev,
        firstName: c.firstName || prev.firstName,
        lastName: c.lastName || prev.lastName,
        surname: c.patronymic || prev.surname,
        contacts: c.contact || prev.contacts,
        groupId: c.suggestedGroupId?.toString() || prev.groupId,
      }));
      if (c.referrer) {
        setReferrerOption({
          value: c.referrer.id,
          label: formatStudentName(c.referrer),
          student: c.referrer,
        });
      }
    }).catch(() => {});
  }, [fromConsultationId, token]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.groupId) {
      errors.groupId = 'Group selection is required';
    }

    if (!formData.startDate) {
      errors.startDate = 'First payment date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    const studentData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      surname: formData.surname.trim() || undefined,
      groupId: parseInt(formData.groupId),
      contacts: formData.contacts.trim() || undefined,
      startDate: formData.startDate,
      referredByStudentId: referrerOption?.value,
    };

    try {
      const result = await dispatch(createStudentAsync(studentData)).unwrap();
      if (fromConsultationId && result.student?.id) {
        await axios.put(
          `${API_BASE_URL}/consultations/${fromConsultationId}`,
          { studentId: result.student.id },
          { headers: token ? { Authorization: `Bearer ${token}` } : {} }
        ).catch(() => {});
      }
      navigate(result.student?.id ? `/students/edit/${result.student.id}` : '/students');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/students');
  };

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Create New Student</h1>
          <p>Add a new student to the system</p>
        </div>
        <button 
          type="button" 
          className="btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Student Information</h2>
          
          <div className="form-row-compact three-columns">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`form-input ${formErrors.firstName ? 'error' : ''}`}
                required
              />
              {formErrors.firstName && <span className="error-text">{formErrors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`form-input ${formErrors.lastName ? 'error' : ''}`}
                required
              />
              {formErrors.lastName && <span className="error-text">{formErrors.lastName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="surname">Surname</label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter surname (optional)"
              />
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="groupId">Group *</label>
            <select
              id="groupId"
              name="groupId"
              value={formData.groupId}
              onChange={handleInputChange}
              className={`form-select ${formErrors.groupId ? 'error' : ''}`}
              required
            >
              <option value="">Select a group</option>
              {groups.map(group => (
                <option key={group.id} value={group.id}>
                  {group.name || `${group.level.toUpperCase()} Group`}
                </option>
              ))}
            </select>
            {formErrors.groupId && <span className="error-text">{formErrors.groupId}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="contacts">Contacts</label>
            <textarea
              id="contacts"
              name="contacts"
              value={formData.contacts}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter contact information (phone, email, etc.)"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="startDate">First Payment Date *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className={`form-input ${formErrors.startDate ? 'error' : ''}`}
              required
            />
            {formErrors.startDate && <span className="error-text">{formErrors.startDate}</span>}
          </div>

          <div className="form-group">
            <label>Brought by</label>
            <StudentReferrerPicker
              value={referrerOption}
              onChange={setReferrerOption}
            />
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading}
          >
            {loading ? 'Creating...' : 'Create Student'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateStudent; 